import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = {
  new: { label: "New", cls: "bg-[#F59E0B] text-black" },
  called: { label: "Called", cls: "bg-sky-500 text-black" },
  booked: { label: "Booked", cls: "bg-green-500 text-black" },
  done: { label: "Done", cls: "bg-neutral-500 text-white" },
};

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem("uw_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    const token = localStorage.getItem("uw_token");
    if (!token) return navigate("/admin/login");
    try {
      const { data } = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) return logout();
      setLeads([]);
    }
  }, [navigate, logout]);

  const setStatus = async (id, status) => {
    const token = localStorage.getItem("uw_token");
    try {
      const { data } = await axios.patch(
        `${API}/quotes/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
    } catch (err) {
      if (err.response?.status === 401) return logout();
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-leads-page" className="min-h-screen bg-[#0A0A0A] text-white px-6 lg:px-8 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#F59E0B] mb-2">Owner Dashboard</p>
            <h1 className="font-display uppercase text-4xl tracking-tight">Quote Requests</h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:border-[#F59E0B] transition-colors duration-200"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-[#F59E0B] text-black px-4 py-2.5 text-sm font-bold hover:bg-[#D97706] transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
        {leads === null ? (
          <p data-testid="leads-loading" className="text-neutral-400">Loading leads...</p>
        ) : leads.length === 0 ? (
          <div data-testid="leads-empty" className="border border-white/10 p-16 flex flex-col items-center gap-4 text-neutral-400">
            <Inbox className="w-10 h-10 text-[#F59E0B]" />
            <p>No quote requests yet. Share your website to start getting leads.</p>
          </div>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <Table data-testid="leads-table">
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-neutral-400">Date</TableHead>
                  <TableHead className="text-neutral-400">Name</TableHead>
                  <TableHead className="text-neutral-400">Phone</TableHead>
                  <TableHead className="text-neutral-400">Service</TableHead>
                  <TableHead className="text-neutral-400">Details</TableHead>
                  <TableHead className="text-neutral-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-neutral-400 whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}
                    </TableCell>
                    <TableCell className="font-bold">{lead.name}</TableCell>
                    <TableCell>
                      <a href={`tel:${lead.phone}`} className="text-[#F59E0B] hover:underline">{lead.phone}</a>
                    </TableCell>
                    <TableCell>{lead.service}</TableCell>
                    <TableCell className="text-neutral-400 max-w-xs">{lead.message || "—"}</TableCell>
                    <TableCell>
                      <select
                        data-testid={`lead-status-${lead.id}`}
                        value={lead.status || "new"}
                        onChange={(e) => setStatus(lead.id, e.target.value)}
                        className={`${STATUSES[lead.status || "new"].cls} text-xs font-bold uppercase tracking-wider px-2 py-1.5 rounded-none border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#F59E0B]`}
                      >
                        {Object.entries(STATUSES).map(([value, s]) => (
                          <option key={value} value={value} className="bg-[#171717] text-white">
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
